import React, { useState, useEffect } from 'react';
import { affiliateService } from '../services/affiliateService';

export default function AffiliateProducts({ listing }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search Amazon products using the affiliate service
  const searchAmazonProducts = async (listing) => {
    setLoading(true);
    
    try {
      const keywords = affiliateService.generateSearchKeywords(listing);
      const searchResults = await affiliateService.searchProducts(keywords, 3);
      
      // Transform the results to match our component's expected format
      const transformedProducts = searchResults.map(product => ({
        id: product.asin,
        title: product.title,
        price: product.formattedPrice,
        originalPrice: product.formattedOriginalPrice,
        rating: product.rating,
        reviews: product.reviewCount,
        image: product.imageUrl,
        affiliateUrl: product.affiliateUrl,
        prime: product.prime,
        availability: product.availability
      }));
      
      setProducts(transformedProducts);
    } catch (error) {
      console.error('Error fetching affiliate products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle affiliate link clicks
  const handleAffiliateClick = (product) => {
    affiliateService.trackClick(product.id, product.title);
    // The link will open in a new tab automatically
  };

  useEffect(() => {
    if (listing) {
      searchAmazonProducts(listing);
    }
  }, [listing]);

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Similar Products on Amazon</h3>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <span>Powered by</span>
            <span className="font-semibold text-orange-600">Amazon</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
              <div className="w-full h-32 bg-slate-200 rounded-lg mb-3"></div>
              <div className="h-4 bg-slate-200 rounded mb-2"></div>
              <div className="h-4 bg-slate-200 rounded w-3/4 mb-3"></div>
              <div className="h-6 bg-slate-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!products.length) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Similar Products on Amazon</h3>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <span>Powered by</span>
          <span className="font-semibold text-orange-600">Amazon</span>
        </div>
      </div>
      
      <p className="text-sm text-slate-600 mb-4">
        Can't find what you're looking for? Check out these similar items on Amazon!
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {products.map((product) => (
          <a
            key={product.id}
            href={product.affiliateUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="affiliate-product-card group"
            onClick={() => handleAffiliateClick(product)}
          >
            <div className="relative mb-3">
              <img
                src={product.image}
                alt={product.title}
                className="w-full h-32 object-cover rounded-lg"
                loading="lazy"
              />
              {product.prime && (
                <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  Prime
                </div>
              )}
            </div>
            
            <div className="flex-1 flex flex-col">
              <h4 className="font-medium text-slate-900 text-sm mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                {product.title}
              </h4>
              
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-3 h-3 ${
                        i < Math.floor(product.rating)
                          ? 'text-yellow-400'
                          : 'text-slate-300'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-xs text-slate-500">
                  {product.rating} ({product.reviews.toLocaleString()})
                </span>
              </div>
              
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg font-bold text-green-600">{product.price}</span>
                {product.originalPrice && (
                  <span className="text-sm text-slate-500 line-through">{product.originalPrice}</span>
                )}
              </div>
              
              <div className="text-xs text-slate-500 mb-3">{product.availability}</div>
            </div>
            
            <div className="mt-auto">
              <div className="flex items-center justify-center w-full py-2 bg-gradient-to-r from-orange-400 to-orange-500 text-white text-sm font-medium rounded-lg hover:from-orange-500 hover:to-orange-600 transition-all duration-200">
                <span>View on Amazon</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </div>
            </div>
          </a>
        ))}
      </div>
      
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-xs text-blue-700 text-center">
          💡 <strong>Affiliate Disclosure:</strong> We may earn a commission when you purchase through our links. 
          This helps support our platform and keeps it free for all users.
        </p>
      </div>
    </div>
  );
}
